<template>
  <div ref="wrapperElement" :class="$style.selfcareDropdownMenu" @mouseleave="hide">
    <lv-button
      v-for="item in menuItems"
      :key="item.name"
      :to="item.click ? null : $path($localePath(item.to))"
      :disabled="item.disabled"
      :class="[$style.selfcareDropdownMenuItem]"
      styling="link-primary-version2"
      @click="onClick(item)"
    >
      <span v-text="item.name" />
    </lv-button>
  </div>
</template>

<script setup>
  import { computed, onBeforeUnmount, ref, watch } from 'vue';
  import { navigateTo, useNuxtApp } from 'nuxt/app';
  import { LvButton } from '~~/common/components/loginet-vue-shop/index';
  import { URL_PREFIX_WITHOUT_SLASH } from '~~/common/config/UrlConfig';
  import { clickLogout } from '~~/common/utils/logout';
  import { useOrderStore } from '~~/common/stores/order';
  import { useCheckoutStore } from '~~/common/stores/checkout';
  import { createHeaderIdFromToPath } from '~~/common/utils/analyticsHelpers';
  import { PUSH_SELECT_HEADER_ITEM } from '~~/common/plugins/aw-analytics';

  const nuxtApp = useNuxtApp();
  const props = defineProps({
    show: {
      type: Boolean,
      required: true,
    },
  });
  const emits = defineEmits(['update:show']);

  const wrapperElement = ref(null);

  const menuItems = computed(() => {
    const prefix = URL_PREFIX_WITHOUT_SLASH;
    const {
      $awt,
      $path,
      $localePath,
    } = nuxtApp;
    const orderStore = useOrderStore();
    const checkoutStore = useCheckoutStore();
    const logoutBtn = {
      name: 'aw.common.header.user.logout',
      click: clickLogout,
    };
    return (__AW_IS_SHOP__
      ? [
        {
          name: 'aw.common.header.selfcare_dropdown_menu.loyalty_program',
          to: `${prefix}-profile-loyalty-program`,
        },
        {
          name: 'aw.common.header.selfcare_dropdown_menu.orders',
          to: `${prefix}-profile-orders`,
        },
        {
          name: 'aw.common.header.selfcare_dropdown_menu.saved_baskets',
          to: `${prefix}-profile-saved-baskets`,
        },
        {
          name: 'aw.common.header.selfcare_dropdown_menu.user_details',
          to: `${prefix}-profile-data`,
        },
        logoutBtn,
      ]
      : [
        {
          name: 'awd.common.header.selfcare_dropdown_menu.start_order',
          disabled: Boolean(orderStore.getModStrategy),
          click: async () => {
            await Promise.all([
              checkoutStore.deleteCheckout(),
              checkoutStore.deleteReservations(),
              orderStore.removeAllItem('DEALER'),
            ]);
            navigateTo($path($localePath('/')));
          },
        },
        {
          name: 'awd.common.header.selfcare_dropdown_menu.orders',
          to: '/orders-list',
        },
        {
          name: 'awd.common.header.selfcare_dropdown_menu.label_print',
          to: '/label-print',
        },
        {
          name: 'awd.common.header.selfcare_dropdown_menu.quick_view',
          to: '/results/quick-view',
        },
        {
          name: 'awd.common.header.selfcare_dropdown_menu.my_results',
          to: '/results/my-results',
        },
        logoutBtn,
      ]
    ).map((o) => {
      o.name = $awt(o.name);
      return o;
    });
  });
  let timeoutId = null;

  watch(() => props.show, (newVal, oldVal) => {
    if (newVal && !oldVal) {
      cleanup();
      timeoutId = setTimeout(() => {
        cleanup();
        document.body.addEventListener('click', hideDropdownOutsideClick);
      }, 400);
    }
  }, { immediate: true });

  onBeforeUnmount(cleanup);

  function cleanup () {
    clearTimeout(timeoutId);
    document.body.removeEventListener('click', hideDropdownOutsideClick);
  }

  function hideDropdownOutsideClick (event) {
    if (wrapperElement.value && !wrapperElement.value?.contains?.(event.target)) {
      hide();
    }
  }

  function onClick (item) {
    nuxtApp.$awAnalytics[PUSH_SELECT_HEADER_ITEM]({
      headerName: item.name,
      headerId: createHeaderIdFromToPath(item.to ? nuxtApp.$localePath(item.to) : ''),
    });
    if (typeof item?.click === 'function') {
      item.click();
    }
    hide({ focusInside: true });
  }

  function hide ({ focusInside } = {}) {
    cleanup();
    emits('update:show', false, { focusInside });
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
@use "sass:color";

.selfcareDropdownMenu {
  font-family: $secondary-font;
  width: 256px;
  padding: 8px 8px 0;
  color: $color-text-primary;
  border: none;
  border-radius: 12px;
  background-color: $color-background-4;
  box-shadow: 0 8px 24px 0 color.change($color-olive-750, $alpha: 0.12);

  &Item {
    font-size: 14px;
    font-weight: $font-weight-normal-v2;
    line-height: 20px;
    padding: 8px;

    text-align: left;
    border-radius: 8px;
  }
}
</style>
